import {
    getLocation,
    push,
    LOCATION_CHANGE,
    replace,
} from 'connected-react-router';
import { stringify, parse } from 'query-string';
import _ from 'lodash';
import searchStatsService from '../../services/searchstatsService';
import { setLoading, setErrorMessage } from './ui';
import { logout } from './user';
import { getDefaultFrom, getDefaultTo, getGroupingPeriod } from '../storeUtils/dateUtils';
import routeConstants from '../../Routes/routeConstants';
import { isUnauthorized } from '../../services/utils/responseValidator';

// ACTIONS
const SET_FORMATED_SEARCH_DATA = 'SET_FORMATED_SEARCH_DATA';
const CLEAR_SEARCH_DATA = 'CLEAR_SEARCH_DATA';
const CLEAR_SEARCH_FORM = 'CLEAR_SEARCH_FORM';
const GROUP_SEARCH_BY = 'GROUP_SEARCH_BY';
const SET_SEARCH_FORM_STATE = 'SET_SEARCH_FORM_STATE';
const SET_SEARCH_FORM_FIELD = 'SET_SEARCH_FORM_FIELD';
const SET_SEARCH_LOCATION = 'SET_SEARCH_LOCATION';
const SET_SELECTED_WHAT_SUGGESTIONS = 'SET_SELECTED_WHAT_SUGGESTIONS';
const SET_SELECTED_WHERE_SUGGESTIONS = 'SET_SELECTED_WHERE_SUGGESTIONS';

const initialState = {
    formState: {
        what: '',
        where: '',
        from: getDefaultFrom(),
        to: getDefaultTo(),
        allPlaces: false,
    },
    selectedWhatSuggestions: [],
    selectedWhereSuggestions: [],
    groupBy: 'MONTH',
    location: {
        pathname: routeConstants.SEARCH_STATS,
        hash: '',
        search: '',
    },
    stats: {},
};

// REDUCER
const reducer = (state = initialState, action) => {
    switch (action.type) {
    case SET_FORMATED_SEARCH_DATA: {
        const clone = _.cloneDeep(action.value);
        const {
            graphDataPoint,
            graphDate,
        } = action.value;

        return Object.assign({}, state, {
            stats: clone,
            graphDataPoint: graphDataPoint[state.groupBy.toLowerCase()],
            graphDate: graphDate[state.groupBy.toLowerCase()],
        });
    }
    case GROUP_SEARCH_BY: {
        const {
            graphDataPoint,
            graphDate,
        } = state.stats;

        return Object.assign({}, state, {
            groupBy: action.value,
            graphDataPoint: graphDataPoint && graphDataPoint[action.value.toLowerCase()],
            graphDate: graphDate && graphDate[action.value.toLowerCase()],
        });
    }
    case CLEAR_SEARCH_DATA: {
        return Object.assign({}, state, {
            stats: {},
            graphDataPoint: null,
            graphDate: null,
        });
    }
    case CLEAR_SEARCH_FORM: {
        return Object.assign({}, state, {
            formState: initialState.formState,
            selectedWhatSuggestions: [],
            selectedWhereSuggestions: [],
        });
    }
    case SET_SEARCH_FORM_STATE: {
        return Object.assign({}, state, {
            formState: action.value,
        });
    }
    case SET_SEARCH_FORM_FIELD: {
        return Object.assign({}, state, {
            formState: { ...state.formState, ...action.value },
        });
    }
    case SET_SEARCH_LOCATION: {
        return Object.assign({}, state, {
            location: action.value,
        });
    }
    case SET_SELECTED_WHAT_SUGGESTIONS: {
        return Object.assign({}, state, {
            selectedWhatSuggestions: action.value,
        });
    }
    case SET_SELECTED_WHERE_SUGGESTIONS: {
        return Object.assign({}, state, {
            selectedWhereSuggestions: action.value,
        });
    }
    case LOCATION_CHANGE: {
        const { pathname, state: actionPayloadState } = action.payload.location || {};
        const isSearchStatsLocation = pathname === routeConstants.SEARCH_STATS;

        if (isSearchStatsLocation) {
            const { searchstats } = actionPayloadState || {};
            const {
                graphDataPoint,
                graphDate,
                selectedWhatSuggestions,
                selectedWhereSuggestions,
                formState,
                groupBy,
                stats,
            } = searchstats || initialState;

            return Object.assign({}, state, {
                stats,
                graphDataPoint,
                graphDate,
                selectedWhatSuggestions,
                selectedWhereSuggestions,
                location: action.payload.location,
                formState,
                groupBy,
            });
        }
        return state;
    }
    default:
        return state;
    }
};

// ACTION CREATORS
const clearData = () => ({
    type: CLEAR_SEARCH_DATA,
});
const clearForm = () => ({
    type: CLEAR_SEARCH_FORM,
});

const groupStatsBy = groupBy => ({
    type: GROUP_SEARCH_BY,
    value: groupBy,
});

const internalSetFormattedData = stats => ({
    type: SET_FORMATED_SEARCH_DATA,
    value: stats,
});

const internalSetFormState = formData => ({
    type: SET_SEARCH_FORM_STATE,
    value: formData,
});

const internalSetSearchLocation = location => ({
    type: SET_SEARCH_LOCATION,
    value: location,
});

const setFormField = field => ({
    type: SET_SEARCH_FORM_FIELD,
    value: field,
});

const setSelectedWhatSuggestions = selected => ({
    type: SET_SELECTED_WHAT_SUGGESTIONS,
    value: selected,
});

const setSelectedWhereSuggestions = selected => ({
    type: SET_SELECTED_WHERE_SUGGESTIONS,
    value: selected,
});

// THUNK
const clearAll = () => (dispatch, getState) => {
    dispatch(clearData());
    dispatch(clearForm());
    const { formState } = getState().searchstats;
    dispatch(push({
        pathname: routeConstants.SEARCH_STATS,
        search: stringify(formState),
        state: getState(),
    }));
};

const getSearchStats = formData => (dispatch, getState) => {
    dispatch(setLoading(true));
    dispatch(clearData());
    dispatch(internalSetFormState(formData));
    return searchStatsService.getSearchStats(formData)
        .then((apiResonse) => {
            dispatch(groupStatsBy(getGroupingPeriod(formData.from, formData.to)));
            dispatch(internalSetFormattedData(apiResonse));
            dispatch(push({
                pathname: routeConstants.SEARCH_STATS,
                search: stringify(formData),
                state: getState(),
            }));
            dispatch(setLoading(false));
            dispatch(internalSetSearchLocation(getLocation(getState())));
        })
        .catch((err) => {
            dispatch(setLoading(false));
            if (isUnauthorized(err.response)) {
                dispatch(logout());
            }
            dispatch(setErrorMessage(err.message));
        });
};

const setInitialSearchFormState = location => (dispatch, getState) => {
    const { formState } = getState().searchstats;
    const formParams = parse(location.search);

    const newFormState = {
        what: formParams.what || formState.what,
        where: formParams.where || formState.where,
        to: formParams.to || formState.to,
        from: formParams.from || formState.from,
        allPlaces: !!formParams.allPlaces && formParams.allPlaces === 'true',
    };

    if (newFormState.what !== '') {
        const selectedWhat = newFormState.what
            .split(',').map(word => ({ label: word, value: word }));

        dispatch(setSelectedWhatSuggestions(selectedWhat));
    }

    if (newFormState.where !== '') {
        const selectedWhere = newFormState.where
            .split(',').map(word => ({ label: word, value: word }));

        dispatch(setSelectedWhereSuggestions(selectedWhere));
    }

    dispatch(internalSetFormState(newFormState));

    dispatch(replace({
        pathname: routeConstants.SEARCH_STATS,
        search: stringify(newFormState),
        state: getState(),
    }));

    if (newFormState.what !== '') {
        return dispatch(getSearchStats(newFormState));
    }
    return null;
};

export {
    initialState,
    getSearchStats,
    setInitialSearchFormState,
    clearData,
    clearForm,
    clearAll,
    setFormField,
    groupStatsBy,
    setSelectedWhatSuggestions,
    setSelectedWhereSuggestions,
};

export default reducer;
